// Filtro de Kalman 2D simplificado pra suavizar coordenadas GPS.
//
// v7 (fev/2026): Cada eixo (lat/lng) tratado independentemente com processo
// linear + medida ruidosa. A variância da medida é derivada da precisão
// reportada pelo watchPosition (accuracy em metros).
//
// Uso:
//   const kf = new GPSKalman();
//   const { latitude, longitude, accuracy } = kf.update(rawPos);
//
// Referência simplificada: https://gist.github.com/scottopolis/8007e4b12e6cd1a3ffb0

export class GPSKalman {
  constructor() {
    this.state = null;      // [lat, lng]
    this.variance = -1;     // metros² — negativo = estado ainda não inicializado
    // Q — process noise: quanto o driver pode "andar" entre pings (m/s).
    // 3 m/s ≈ 10.8 km/h (velocidade urbana média com paradas).
    this.QMetersPerSecond = 3;
    this.lastTs = 0;
  }

  // pos: { latitude, longitude, accuracy?, timestamp? (ms) }
  // Retorna { latitude, longitude, accuracy } corrigidos.
  update(pos) {
    if (!Number.isFinite(pos?.latitude) || !Number.isFinite(pos?.longitude)) {
      return pos;
    }
    // R — variância da medida (m²). Usa accuracy do GPS se disponível, senão fallback 10m.
    const accuracy = Number.isFinite(pos.accuracy) && pos.accuracy > 0 ? pos.accuracy : 10;
    const R = accuracy * accuracy;
    const ts = typeof pos.timestamp === "number" ? pos.timestamp : Date.now();

    if (this.variance < 0) {
      // Inicializa com a primeira medida
      this.state = [pos.latitude, pos.longitude];
      this.variance = R;
      this.lastTs = ts;
      return { ...pos, latitude: pos.latitude, longitude: pos.longitude, accuracy };
    }

    // Predict — infla variância por tempo decorrido
    const dt = Math.max(0.05, (ts - this.lastTs) / 1000); // segundos, mínimo 50ms
    this.variance += dt * this.QMetersPerSecond * this.QMetersPerSecond;
    this.lastTs = ts;

    // Update com nova medida — ganho de Kalman
    const K = this.variance / (this.variance + R);
    this.state[0] += K * (pos.latitude - this.state[0]);
    this.state[1] += K * (pos.longitude - this.state[1]);
    this.variance = (1 - K) * this.variance;

    return {
      ...pos,
      latitude: this.state[0],
      longitude: this.state[1],
      accuracy: Math.sqrt(this.variance),
    };
  }

  reset() {
    this.state = null;
    this.variance = -1;
    this.lastTs = 0;
  }
}

// Distância em metros entre 2 coords (haversine).
export function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371000; // raio da Terra em m
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
